import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { dark } from "@clerk/ui/themes";
import { House, Printer, ShieldCheck, ChatCircleDots, List, X, FirstAid, ChartLineUp } from "@phosphor-icons/react";
import { MfaGate, useIdleSignOut } from "./MfaGate";
import { useApi } from "../lib/api";
import { GROUP_TYPES } from "../lib/constants";
import { BrandLogo } from "./brand/BrandLogo";

const MeCtx = createContext(null);
export const useMe = () => useContext(MeCtx);

const Item = ({ to, icon: Icon, label, testid, color, end }) => (
  <NavLink to={to} end={end} data-testid={testid}
    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`}>
    {Icon ? <Icon size={18} /> : <span className="w-2.5 h-2.5 rounded-sm ml-1" style={{ background: color }} />}
    <span className="truncate">{label}</span>
  </NavLink>
);

function Sidebar({ me, groups, onNav }) {
  return (
    <div className="flex flex-col h-full p-4 gap-1" onClick={onNav}>
      <div className="flex items-center gap-2 px-2 mb-6">
        <BrandLogo tone="dark" size={30} testid="app-logo" />
      </div>
      <Item to="/app" end icon={House} label="Home" testid="nav-home" />
      <Item to="/app/my-page" icon={FirstAid} label="My emergency page" testid="nav-my-page" />
      <Item to="/app/outbox" icon={ChatCircleDots} label="WhatsApp outbox" testid="nav-outbox" />
      <div className="overline px-3 mt-6 mb-2">Groups</div>
      {groups.map((g) => <Item key={g.id} to={`/app/g/${g.id}`} label={g.name} color={GROUP_TYPES[g.type]?.color} testid={`nav-group-${g.id}`} />)}
      <Item to="/app/onboarding" label="+ New group" color="#334155" testid="nav-new-group" />
      {["admin", "print_team"].includes(me?.role) && (<><div className="overline px-3 mt-6 mb-2">Internal</div>
        <Item to="/app/print" icon={Printer} label="Print console" testid="nav-print" /></>)}
      {me?.role === "admin" && <><Item to="/app/owner" icon={ChartLineUp} label="Owner overview" testid="nav-owner" />
        <Item to="/app/admin" icon={ShieldCheck} label="Admin console" testid="nav-admin" /></>}
      <div className="mt-auto flex items-center gap-3 px-2 pt-4 border-t border-slate-800" onClick={(e) => e.stopPropagation()}>
        <UserButton appearance={{ theme: dark }} />
        <div className="text-xs text-slate-400 truncate" data-testid="current-user-email">{me?.email}</div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [mfa, setMfa] = useState(false);
  const loc = useLocation();
  const reload = useCallback(async () => {
    const [m, g, s] = await Promise.all([api.get("/me"), api.get("/groups"), api.get("/2fa/status")]);
    setMe(m.data); setGroups(g.data); setMfa(s.data.required && !s.data.verified);
  }, [api]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const on = () => setMfa(true);
    window.addEventListener("bsafe-mfa", on);
    return () => window.removeEventListener("bsafe-mfa", on);
  }, []);
  const idle = groups.filter((g) => ["company", "school", "organisation"].includes(g.type) && g.settings?.idle_minutes).map((g) => g.settings.idle_minutes);
  useIdleSignOut(idle.length ? Math.min(...idle) : 0);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  if (mfa) return <MfaGate onDone={() => { setMfa(false); window.location.reload(); }} />;
  return (
    <MeCtx.Provider value={{ me, setMe, groups, reload }}>
      <div className="min-h-screen grain flex">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-800 bg-[#081022] sticky top-0 h-screen"><Sidebar me={me} groups={groups} /></aside>
        {open && <div className="fixed inset-0 z-40 lg:hidden bg-black/60" onClick={() => setOpen(false)}>
          <aside className="w-72 h-full bg-[#081022] border-r border-slate-800" onClick={(e) => e.stopPropagation()}><Sidebar me={me} groups={groups} /></aside></div>}
        <main className="flex-1 min-w-0 relative z-10">
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-[#081022] sticky top-0 z-30">
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>{open ? <X size={22} /> : <List size={22} />}</button>
            <span className="font-display font-black">bsafe<span className="text-red-500">.today</span></span><UserButton appearance={{ theme: dark }} />
          </div>
          <div className="px-4 sm:px-8 lg:px-12 py-8 max-w-[1400px]">{me ? <Outlet /> : <div className="text-slate-400" data-testid="app-loading">Loading…</div>}</div>
        </main>
      </div>
    </MeCtx.Provider>
  );
}
